import { Controller } from '@hotwired/stimulus'
import { tns } from 'tiny-slider'
import { get } from '@rails/request.js'
import isMobile from '~/utils/navigator'

export default class extends Controller {
  static values = {
    centerSlideId: { type: Number },
    url: { type: String }
  }

  static targets = ['carousel', 'prev', 'next', 'nav', 'navDots']

  connect () {
    this.initializeSlider()
    if (!isMobile) {
      this._debouncedResize = this._debounce(() => this.#reinitSlider(), 1000)
      window.addEventListener('resize', this._debouncedResize)
    }
    if (isMobile) {
      this.#setActiveSlide()
    }
  }

  disconnect () {
    if (!isMobile) {
      window.removeEventListener('resize', this._debouncedResize)
    }
    this.#destroySlider()
  }

  initializeSlider () {
    this.#removeExistingNav()
    const slides = Array.from(this.carouselTarget.children)
    const centerIndex = slides.findIndex(slide =>
      slide.getAttribute('data-commitment-id') === this.centerSlideIdValue.toString()
    )
    const validCenterIndex = centerIndex !== -1 ? centerIndex : 0

    this.slider = tns(this.sliderConfig(validCenterIndex))
    this.slider.events.on('indexChanged', info => {
      this.#setActiveSlide(info)
      this.#fetchIntentions()
    })

    this.#bindSlideClickEvents(slides)
  }

  #reinitSlider () {
    this.#destroySlider()
    this.initializeSlider()
  }

  #destroySlider () {
    if (this.slider && this.slider.destroy) {
      this.slider.destroy()
    }
  }

  sliderConfig (startIndex = 1) {
    const width = window.innerWidth
    const useFixedWidth = width < 640

    return {
      container: this.carouselTarget,
      mode: 'carousel',
      startIndex,
      items: 3,
      center: true,
      gutter: 0,
      slideBy: 1,
      loop: false,
      swipeAngle: false,
      edgePadding: 0,
      nav: false,
      navAsThumbnails: true,
      navPosition: 'bottom',
      nextButton: this.hasNextTarget ? this.nextTarget : null,
      prevButton: this.hasPrevTarget ? this.prevTarget : null,
      autoplay: false,
      speed: 300,
      // I do so instead of responsive because Tiny-slider doesn't allow you to reset fixedWidth once it's set - if it's been set in even one breakpoint, it will permanently affect layout (Mobile issue).
      ...(useFixedWidth
        ? { fixedWidth: 355, autoWidth: false }
        : { autoWidth: true }),
      responsive: {
        1024: { nav: true }
      },
      onInit: info => {
        this.sliderInfo = info
        this.#setActiveSlide(info)
        this.#setNavPosition()
        this.element.classList.add('ipm_slider-ready')
      }
    }
  }

  #setActiveSlide (info = null) {
    if (!this.slider) return
    const sliderInfo = info || this.slider.getInfo()
    if (!sliderInfo?.slideItems) return

    const slides = Array.from(sliderInfo.slideItems)
    const centerIndex = sliderInfo.index + Math.floor(sliderInfo.items / 2)
    const centerSlide = slides[centerIndex]

    this.#updateCommitmentId(centerSlide)
    this.#updateSlideClasses(slides, centerIndex)
  }

  #updateSlideClasses (slides, centerIndex) {
    slides.forEach((slide, index) => {
      const isActive = index === centerIndex
      slide.classList.toggle('ipm_slider__item-active', isActive)
      slide.classList.toggle('ipm_slider__item-inactive', !isActive)
      slide.classList.toggle('translate-left', index < centerIndex)
      slide.classList.toggle('translate-right', index > centerIndex)
    })
  }

  #setNavPosition () {
    const navContainer = this.element.querySelector('.tns-nav')
    if (navContainer && this.hasNavDotsTarget) {
      this.navDotsTarget.appendChild(navContainer)
    }
  }

  #removeExistingNav () {
    const existingNav = this.element.querySelector('.tns-nav')
    if (existingNav) existingNav.remove()
  }

  #updateCommitmentId (slide) {
    if (!slide?.hasAttribute('data-commitment-id')) return

    this.centerSlideIdValue = slide.getAttribute('data-commitment-id')
  }

  #bindSlideClickEvents (slides) {
    slides.forEach((slide, index) => {
      slide.addEventListener('click', () => {
        this.slider.goTo(index)
      })
    })
  }

  async #fetchIntentions () {
    if (!this.urlValue || isNaN(this.centerSlideIdValue)) return

    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set('higher_commitment_id', this.centerSlideIdValue)
    await get(url.toString(), {
      headers: { Accept: 'text/vnd.turbo-stream.html' },
      responseKind: 'turbo-stream'
    })
  }

  _debounce (fn, delay) {
    let timeout
    return (...args) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => fn.apply(this, args), delay)
    }
  }
}
